/**
 * Letter / unlock CONTENT model + fixtures.
 *
 * The letter viewer and the unlock sheet render content — key-point bullets (the free teaser), the
 * signed letter body, referee metadata, a reference id and the entitlement (locked vs unlocked). As
 * of WP-F11a this is REAL on both backings:
 *   - The MOCK client builds the demo content from the ledger card and models the entitlement in an
 *     in-memory unlock store (so a one-tap / paid unlock flips a `ready` reference to unlocked and
 *     the letter reveals, offline).
 *   - The HTTP client reads the real WP-B19 content chokepoint (`GET /api/references/{id}/content/
 *     {tier}`): `keypoints` is free (the teaser), `letter` is entitlement-gated (403 → locked). The
 *     `alreadyUnlocked` fact comes from `GET /api/references/{id}/unlock`, never the ledger card
 *     (the Phase-1 ledger wire still tops out at `signed` and carries no entitlement — DESIGN-NOTES).
 *
 * This module holds the one content view-model + the pure builders/parsers shared by both backings,
 * kept transport-free so they are unit-testable. The MOCK letter text is the canonical demo letter
 * (design-spec/04-copy "Example letter"); the HTTP letter body is the real signed body, parsed by
 * {@link parseLetterBody} into the same salutation/paragraphs shape the viewer renders.
 */
import type { LedgerCard } from './ledger';

/** The signed letter body — salutation + paragraphs + the Caveat-rendered signature name. */
export interface LetterBody {
  salutation: string;
  paragraphs: string[];
  signatureName: string;
}

/**
 * Everything the letter viewer / unlock sheet render for one reference. `letter` is present ONLY
 * when `unlocked` — the gated body is never shipped to a locked view (the teaser `keyPoints` are).
 * When `unlocked` is true but `letter` is null, the reference is entitled but not signed yet (the
 * server's "authorized, content pending" state) — the viewer shows a calm "being finalised" note.
 */
export interface ReferenceContent {
  requestId: string;
  refereeName: string;
  /** e.g. "Former Manager" — the honest, letter-facing role line (from the signer snapshot when unlocked). */
  refereeRole: string | null;
  company: string | null;
  /** The free key-point bullets (proof of substance; always shown, locked or not). */
  keyPoints: string[];
  /** Public verification id — the real `verificationCode` once unlocked, else a stable placeholder. */
  referenceId: string;
  /** ISO signed date, or null if not yet signed. */
  signedDate: string | null;
  /** The entitlement: locked (`ready`) shows the teaser + unlock sheet, unlocked shows the letter. */
  unlocked: boolean;
  /** The gated letter body — non-null iff {@link unlocked} AND the reference is signed. */
  letter: LetterBody | null;
  /** Whether the candidate's base free-unlock credit can unlock this reference right now (one-tap). */
  freeCreditAvailable: boolean;
  /** Whether an earned referral bonus can unlock this reference right now (one-tap). */
  referralBonusAvailable: boolean;
}

/** The free teaser bullets (design-spec/05 Screen 03 + demo content). Same three for every seed. */
const KEY_POINTS: readonly string[] = [
  'Led the redesign under a tight deadline',
  'Calm under pressure, carried the team',
  'Would work with her again, gladly',
];

/** The honest, letter-facing role line. The Phase-1 wire carries no relationship field on the
 * ledger row, so the demo uses the canonical example letter's role ("Former Manager"). */
const DEMO_ROLE = 'Former Manager';

/**
 * The canonical demo letter body (04-copy "Example letter"), personalised to the referee. The body
 * is demo content — every seeded reference reuses it with the referee's own name and company.
 */
function letterFor(refereeName: string, company: string | null): LetterBody {
  const place = company ? `at ${company}` : 'over three years';
  return {
    salutation: 'To whom it may concern,',
    paragraphs: [
      `I managed Jane Doe for three years ${place} and recommend her without hesitation. She led our redesign under a punishing deadline, stayed calm when it counted, and brought the whole team with her.`,
      'I would welcome the chance to work with her again.',
    ],
    signatureName: refereeName,
  };
}

/**
 * A deterministic `BMW-XXXX` reference id from a request id — stable across reloads so the seal's
 * verification id never changes for a given reference. Uppercase base-36, four chars.
 */
export function referenceIdFor(requestId: string): string {
  let h = 0;
  for (let i = 0; i < requestId.length; i++) h = (Math.imul(31, h) + requestId.charCodeAt(i)) | 0;
  const code = (h >>> 0).toString(36).toUpperCase().padStart(4, '7').slice(-4);
  return `BMW-${code}`;
}

/** The one-tap unlock methods currently available for a reference (from the unlock-options endpoint). */
export interface UnlockAvailability {
  freeCreditAvailable: boolean;
  referralBonusAvailable: boolean;
}

/**
 * Build the MOCK content view-model for a reference. `unlocked` is passed in (the mock's in-memory
 * entitlement store is the source of truth, not the card state — a one-tap / paid unlock flips a
 * `ready` reference to unlocked mid-session). `unlocked` → the canonical demo letter; locked → the
 * teaser only. `availability` carries the free/referral one-tap options the unlock sheet renders.
 */
export function buildReferenceContent(
  card: LedgerCard,
  unlocked: boolean,
  availability: UnlockAvailability,
): ReferenceContent {
  return {
    requestId: card.id,
    refereeName: card.refereeName,
    refereeRole: DEMO_ROLE,
    company: card.company,
    keyPoints: [...KEY_POINTS],
    referenceId: referenceIdFor(card.id),
    signedDate: card.signedAt,
    unlocked,
    letter: unlocked ? letterFor(card.refereeName, card.company) : null,
    freeCreditAvailable: unlocked ? false : availability.freeCreditAvailable,
    referralBonusAvailable: unlocked ? false : availability.referralBonusAvailable,
  };
}

/** The signed-letter fields the HTTP content chokepoint returns for the `letter` tier (WP-B19). */
export interface SignedLetterFields {
  body: string;
  signerName: string;
  signerCompany: string | null;
  signerRelationship: string | null;
  verificationCode: string;
  signedAt: string;
}

/**
 * Split a signed letter body (verbatim, `plain`/`markdown`) into the salutation + paragraphs the
 * viewer renders. A leading salutation line (short, ends in a comma, or opens "To whom / Dear / Kia
 * ora …") is lifted out; everything else becomes paragraphs on blank-line boundaries. Defensive: an
 * empty body yields no paragraphs (the viewer shows the "being finalised" note), and a single-block
 * body renders as one paragraph. Markdown is not rich-rendered at MVP — reference letters are prose,
 * and rendering the text verbatim is honest; the signature flourish is drawn separately by the viewer.
 */
export function parseLetterBody(body: string, signatureName: string): LetterBody {
  const blocks = body
    .replace(/\r\n?/g, '\n')
    .split(/\n\s*\n/)
    .map((b) => b.trim())
    .filter(Boolean);

  let salutation = '';
  let paragraphs = blocks;
  if (blocks.length > 1 && isSalutationLine(blocks[0])) {
    salutation = blocks[0];
    paragraphs = blocks.slice(1);
  }
  return { salutation, paragraphs, signatureName };
}

/** A short opener that reads as a salutation rather than a body paragraph. */
function isSalutationLine(line: string): boolean {
  if (line.length > 48 || line.includes('\n')) return false;
  return /,\s*$/.test(line) || /^(to whom|dear\b|hi\b|hello\b|kia ora|t[eē]n[aā])/i.test(line);
}

/**
 * Build the HTTP content view-model. Referee identity + the signed date come from the ledger card
 * (locked) or the signer snapshot (unlocked, authoritative); the words + entitlement come from the
 * content/unlock endpoints. `letter` is null when locked, or when unlocked-but-not-yet-signed (the
 * server returns a stub for that "authorized, content pending" state → the viewer shows a note).
 */
export function buildHttpContent(params: {
  card: LedgerCard;
  keyPoints: string[];
  unlocked: boolean;
  availability: UnlockAvailability;
  letter: SignedLetterFields | null;
}): ReferenceContent {
  const { card, keyPoints, unlocked, availability, letter } = params;
  return {
    requestId: card.id,
    refereeName: letter?.signerName || card.refereeName,
    refereeRole: letter?.signerRelationship ?? null,
    company: letter?.signerCompany ?? card.company,
    keyPoints: [...keyPoints],
    referenceId: letter?.verificationCode || referenceIdFor(card.id),
    signedDate: letter?.signedAt ?? card.signedAt,
    unlocked,
    letter: letter ? parseLetterBody(letter.body, letter.signerName) : null,
    freeCreditAvailable: unlocked ? false : availability.freeCreditAvailable,
    referralBonusAvailable: unlocked ? false : availability.referralBonusAvailable,
  };
}
